import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Loader2, FileText, Calendar, Database, Hash } from 'lucide-react';
import { knowledgeBaseApi } from '@/lib/api';
import { formatDate } from '@/utils/dateFormatter';
import type { KbDocumentContent, KbDocument } from '@/types/knowledge-base';
import ReactMarkdown from 'react-markdown';

interface DocumentViewerModalProps {
  open: boolean;
  onClose: () => void;
  document: KbDocument | null;
  collectionId: string;
}

export function DocumentViewerModal({
  open,
  onClose,
  document,
  collectionId,
}: DocumentViewerModalProps) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [documentContent, setDocumentContent] = useState<KbDocumentContent | null>(null);
  const [showChunks, setShowChunks] = useState(false);

  // Fetch document content when modal opens
  useEffect(() => {
    if (open && document) {
      fetchDocumentContent();
    } else {
      // Reset state when modal closes
      setDocumentContent(null);
      setShowChunks(false);
      setError(null);
    }
  }, [open, document?.id]);

  const fetchDocumentContent = async () => {
    if (!document) return;

    setLoading(true);
    setError(null);

    try {
      const response = await knowledgeBaseApi.getDocumentContent(collectionId, document.id);
      setDocumentContent(response.data?.data || response.data);
    } catch (err: any) {
      console.error('Failed to fetch document content:', err);
      setError(err.response?.data?.message || 'Failed to load document content');
    } finally {
      setLoading(false);
    }
  };

  const formatBytes = (bytes: number) => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const isMarkdownFile = (fileType: string) => {
    return ['md', 'markdown'].includes(fileType.toLowerCase());
  };

  const renderContent = () => {
    if (!documentContent) return null;

    if (showChunks) {
      // Render chunks view
      return (
        <div className="space-y-4">
          {documentContent.chunks.map((chunk, index) => (
            <div
              key={chunk.id}
              className="border rounded-lg p-4 bg-muted/30"
            >
              <div className="flex items-center justify-between mb-2">
                <Badge variant="outline" className="text-xs">
                  Chunk {chunk.chunkIndex + 1}
                </Badge>
                <span className="text-xs text-muted-foreground">
                  {chunk.tokenCount} tokens
                </span>
              </div>
              <div className="text-sm whitespace-pre-wrap font-mono bg-background p-3 rounded border">
                {chunk.content}
              </div>
            </div>
          ))}
        </div>
      );
    }

    // Render original content
    const content = documentContent.originalContent;

    if (isMarkdownFile(documentContent.fileType)) {
      // Render markdown
      return (
        <div className="prose prose-sm dark:prose-invert max-w-none">
          <ReactMarkdown>{content}</ReactMarkdown>
        </div>
      );
    }

    // Render plain text
    return (
      <pre className="text-sm whitespace-pre-wrap font-mono bg-muted/30 p-4 rounded-lg">
        {content}
      </pre>
    );
  };

  const parseMetadata = (metadataStr: string | undefined) => {
    if (!metadataStr) return null;
    try {
      return JSON.parse(metadataStr);
    } catch {
      return null;
    }
  };

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <DialogContent className="max-w-4xl h-[85vh] flex flex-col overflow-hidden">
        <DialogHeader className="flex-shrink-0">
          <div className="flex items-start justify-between gap-4">
            <div className="flex items-center gap-3 min-w-0">
              <FileText className="w-5 h-5 text-muted-foreground flex-shrink-0" />
              <div className="min-w-0">
                <DialogTitle className="truncate">
                  {document?.name || 'Document Viewer'}
                </DialogTitle>
                {documentContent && (
                  <div className="flex items-center gap-3 mt-1 text-xs text-muted-foreground">
                    <span className="flex items-center gap-1">
                      <Badge variant="secondary" className="text-xs">
                        {documentContent.fileType}
                      </Badge>
                    </span>
                    <span className="flex items-center gap-1">
                      <Database className="w-3 h-3" />
                      {formatBytes(documentContent.fileSizeBytes)}
                    </span>
                    <span className="flex items-center gap-1">
                      <Hash className="w-3 h-3" />
                      {documentContent.chunks.length} chunks
                    </span>
                    <span className="flex items-center gap-1">
                      <Calendar className="w-3 h-3" />
                      {formatDate(documentContent.createdAt)}
                    </span>
                  </div>
                )}
              </div>
            </div>
            {/* Toggle switch */}
            {documentContent && !loading && (
              <div className="flex items-center gap-2 flex-shrink-0">
                <Label htmlFor="view-toggle" className="text-sm text-muted-foreground">
                  Original
                </Label>
                <Switch
                  id="view-toggle"
                  checked={showChunks}
                  onCheckedChange={setShowChunks}
                />
                <Label htmlFor="view-toggle" className="text-sm text-muted-foreground">
                  Chunks
                </Label>
              </div>
            )}
          </div>
        </DialogHeader>

        {/* Metadata display */}
        {documentContent?.metadata && (
          <div className="flex-shrink-0 px-1">
            <div className="text-xs text-muted-foreground mb-1">Metadata:</div>
            <div className="bg-muted/30 rounded p-2 text-xs font-mono overflow-x-auto">
              {JSON.stringify(parseMetadata(documentContent.metadata), null, 2)}
            </div>
          </div>
        )}

        {/* Content area */}
        <div className="flex-1 min-h-0 mt-4 overflow-y-auto">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
              <span className="ml-2 text-muted-foreground">Loading document...</span>
            </div>
          ) : error ? (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <p className="text-destructive mb-4">{error}</p>
              <Button variant="outline" onClick={fetchDocumentContent}>
                Retry
              </Button>
            </div>
          ) : (
            <div className="pr-2">
              {renderContent()}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
